import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router'
import { useAuthStore } from '@/stores/auth-store'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { Loader2, Brain, AlertCircle } from 'lucide-react'
import { BackgroundWrapper } from '@/components/ui/background-wrapper'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

function GoogleCallbackPage() {
  const navigate = useNavigate()
  const { handleGoogleCallback, error, clearError } = useAuthStore()
  const { code, state, error: oauthError } = Route.useSearch()
  const [isProcessing, setIsProcessing] = useState(true)

  useEffect(() => {
    const processCallback = async () => {
      // Clear any existing errors
      clearError()

      // Check for OAuth error in URL params
      if (oauthError) {
        setIsProcessing(false)
        return
      }

      // Check for authorization code
      if (!code) {
        setIsProcessing(false)
        return
      }

      try {
        await handleGoogleCallback(code, state)
        // On success, redirect to dashboard
        navigate({ to: '/dashboard', replace: true })
      } catch {
        // Error is handled by the store, just stop processing
        setIsProcessing(false)
      }
    }

    processCallback()
  }, [code, state, oauthError, handleGoogleCallback, navigate, clearError])

  if (isProcessing) {
    return (
      <BackgroundWrapper>
        <div className="flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
          <div className="w-full max-w-md">
            <Card className="bg-white dark:bg-white text-black dark:text-black border-gray-200 dark:border-gray-200 shadow-sm dark:shadow-sm">
              <CardContent className="p-6">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white border border-gray-200">
                      <Brain className="h-5 w-5 text-black" />
                    </div>
                    <span className="text-lg font-semibold">ThoughtFabric</span>
                  </div>
                  <div className="space-y-2">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                    <h1 className="text-xl font-semibold">
                      Completing sign in...
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      Please wait while we process your Google sign in.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <div className="text-center text-xs text-balance text-gray-400 mt-6">
              By continuing, you agree to our{' '}
              <a
                href="https://thoughtfabric.ai/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4 hover:text-white transition-colors"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="https://thoughtfabric.ai/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4 hover:text-white transition-colors"
              >
                Privacy Policy
              </a>
              .
            </div>
          </div>
        </div>
      </BackgroundWrapper>
    )
  }

  // Show error state
  return (
    <BackgroundWrapper>
      <div className="flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <Card className="bg-white dark:bg-white text-black dark:text-black border-gray-200 dark:border-gray-200 shadow-sm dark:shadow-sm">
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white border border-gray-200">
                    <Brain className="h-5 w-5 text-black" />
                  </div>
                  <span className="text-lg font-semibold">ThoughtFabric</span>
                </div>

                <div className="space-y-3">
                  <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
                  <h1 className="text-xl font-semibold">Sign in failed</h1>

                  {oauthError && (
                    <div className="bg-destructive/15 border border-destructive/20 text-destructive text-xs px-3 py-2 rounded-md">
                      OAuth Error: {oauthError}
                    </div>
                  )}

                  {error && (
                    <div className="bg-destructive/15 border border-destructive/20 text-destructive text-xs px-3 py-2 rounded-md">
                      {error}
                    </div>
                  )}

                  {!code && !oauthError && (
                    <p className="text-sm text-muted-foreground">
                      No authorization code received from Google.
                    </p>
                  )}
                </div>

                <div className="space-y-2 w-full">
                  <Button
                    onClick={() => navigate({ to: '/auth/login' })}
                    className="w-full bg-black hover:bg-black/90 text-white"
                  >
                    Try again
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate({ to: '/auth/register' })}
                    className="w-full"
                  >
                    Create account
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          <div className="text-center text-xs text-balance text-gray-400 mt-6">
            By continuing, you agree to our{' '}
            <a
              href="https://thoughtfabric.ai/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 hover:text-white transition-colors"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="https://thoughtfabric.ai/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 hover:text-white transition-colors"
            >
              Privacy Policy
            </a>
            .
          </div>
        </div>
      </div>
    </BackgroundWrapper>
  )
}

export const Route = createFileRoute('/auth/google/callback/')({
  validateSearch: z.object({
    code: z.string().optional(),
    state: z.string().optional(),
    error: z.string().optional(),
  }),
  beforeLoad: async () => {
    const { useAuthStore } = await import('@/stores/auth-store')
    const { isAuthenticated } = useAuthStore.getState()

    // If user is already authenticated, redirect to dashboard
    if (isAuthenticated) {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: GoogleCallbackPage,
})
